﻿<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { handleApiRequest } from 'src/api/handleApiRequest';
import { getCanaryActivationNextStep, updateCanaryMeetComputerRequirements } from 'src/api/endpoints/patientWeb';
import { PIQSetupPages, getNextActivationRoute } from '@/services/piqSetupService';
import MultiViewLayout from '@/components/layouts/MultiViewLayout.vue';
import TheAbandonModal from '@/components/TheAbandonModal.vue';

import type { Ref } from 'vue';
import type { NextCanaryPatientActivationStep } from '@/types/webContracts.ts';
import type { ApiResponse } from '@/types/apiTypes.ts';

import { useI18n } from 'vue-i18n';

const { t } = useI18n();

import { useRouter } from 'vue-router';

const router = useRouter();

import { useQuasar } from 'quasar';

const $q = useQuasar();

const hbsVersion: Ref = ref(null);

import { useDashboardStore } from '@/store';

const dashBoard = useDashboardStore();
const { patientConfig } = storeToRefs(dashBoard);

const showAbandon = ref(false);

onMounted(async () => {
  $q.loading.show();
  const hbs: ApiResponse<NextCanaryPatientActivationStep> = await handleApiRequest(() => getCanaryActivationNextStep(), null, $q);
  if (hbs?.result) {
    hbsVersion.value = hbs.result.hbsVersion;
  }
  $q.loading.hide()
});

async function onAbandonClick() {
  showAbandon.value = !showAbandon.value;
}

import TheContactModal from '@/components/TheContactModal.vue';
const showContact = ref(false);
async function onInfoClick() {
  showContact.value = !showContact.value;
}

const deviceSelectionOptions = [
  {
    label: t('selection_an_apple_iphone'),
    value: 'iPhone'
  },
  {
    label: t('selection_an_android'),
    value: 'android'
  }
];

const deviceSelection = ref();

const addAnATag = computed(() => {
  let tel = patientConfig.value?.piqSupportPhone;
  let str = '<a class=\"text-content-trg-inline-link\" href="tel:' + tel + '">' + tel + '</a>';
  return str;
})

async function onNextClick() {
  // send to finalize setup?
  $q.loading.show();
  await getNextActivationRoute($q);
  router.push({ name: 'Dashboard.Home' }).catch(() => { });
  $q.loading.hide();
}

function onSendClick() {
  instructionsLink.value.click();
}
const instructionsLink = ref();

</script>

<template>
  <q-page padding class="canary setup almostComplete">
    <MultiViewLayout :onBackClick="onAbandonClick" back-icon-list="fa-solid fa-house-circle-xmark"
      :box-padding="$zb.enums.BoxPaddingEnum.p303048">
      <template v-slot:title>
        {{ t('hbs_flow_title') }}
      </template>

      <!-- Title -->
      <template #heading>
        {{ t('hbs_handoff_title') }}
      </template>

      <template v-slot:auxiliary>
        <zbm-btn-icon @click="onInfoClick()" icon="fa-regular fa-question-circle" size="24px" text-color="dark-teal" />
      </template>

      <!-- View Unique Content -->
      <template #default>
        <!--        LOGO ROW-->
        <div class="row justify-center">
          <div class="col-4"></div>
          <div class="col-4 text-center mb-20">
            <q-img id="appIcon" src="img/baseStation/appIcon.svg" />
          </div>
          <div class="col-4"></div>
        </div>
        <!--common instructions row-->
        <div class="row text-center mb-20">
          <div class="col-12">
            <p class="subheader-content-subtitle q-mb-xs"> {{ t('hbs_handoff_body1_other') }}</p>
            <p class="subheader-content-subtitle q-pb-none" v-html="t('hbs_handoff_body2', [addAnATag])"></p>
          </div>
        </div>

        <!-- VERSION 1 COPY-->
        <div class="row" v-if="hbsVersion == 'v1'">
          <ol>
            <li> {{ t('hbs_handoff_step1_hbs1_web') }}
              <ol class="mt-12">
                <li>
                  <a class="text-content-trg-inline-link" href="https://windowsbsst.canturio.com/">
                    {{ t('piq_bs_step_five_pc') }}
                  </a>
                </li>
                <li>
                  <a class="text-content-trg-inline-link" href="https://apps.apple.com/us/app/bsst-canary-medical/id1562317016?mt=12">
                    {{ t('piq_bs_step_five_mac') }}
                  </a>
                </li>
              </ol>
            </li>
            <li> {{ t('hbs_handoff_step2_default') }}</li>
            <li>{{ t('hbs_handoff_step3_other') }}</li>
            <li>{{ t('hbs_handoff_step4_web') }}</li>
          </ol>
        </div>
        <!-- VERSION 2 COPY-->
        <div class="row" v-else-if="hbsVersion == 'v2'">
          <ol>
            <li> {{ t('hbs_handoff_step1_hbs2_web') }}<br>
              <zbm-select-with-validation :defaultOptionText="t('drop_down_default')" :options=deviceSelectionOptions
                optionLabel="label" optionValue="value" :outlined=true
                v-model="deviceSelection"></zbm-select-with-validation>
              <!--              <q-select :options="deviceSelectionOptions" />-->
              <div v-if="deviceSelection !== undefined" class="row q-gutter-none appStore-wrapper">

                <div class="col-12 mb-20">
                  <div class="row">
                    <div class="col-5">
                      <p class="font-size-12 all-caps bold">{{ t('hbs_handoff_hbs2_web_step1a') }}</p>
                    </div>
                  </div>
                </div>
                <div class="col">
                  <a v-if="deviceSelection == 'iPhone'"
                    href="https://apps.apple.com/us/app/canary-medical/id1560601946">
                    <img src="img/baseStation/appleAppStore.png" alt="">
                  </a>
                  <a v-else-if="deviceSelection == 'android'"
                    href="https://play.google.com/store/apps/details?id=com.canarymedical.app">
                    <img src="img/baseStation/googleAppStore.png" alt="">
                  </a>
                  <p class="font-size-12">{{ t('app_store_click_this_badge') }}</p>
                </div>
                <div class="col-2 all-caps semibold text-center self-center">
                  {{ t('gen_or') }}
                </div>
                <div class="col">
                  <div class="row">
                    <div class="col text-center">
                      <img v-if="deviceSelection == 'iPhone'" src="img/baseStation/apple-QR.svg" alt="" width="100px"
                        height="100px">
                      <img v-else-if="deviceSelection == 'android'" src="img/baseStation/android-QR.svg" alt=""
                        width="100px" height="100px">
                    </div>
                    <div class="col self-center">
                      <p class="text-micro">
                        {{ t('qr_scan_this_code') }} </p>
                    </div>
                  </div>
                </div>
              </div>
            </li>
            <li> {{ t('hbs_handoff_step2_default') }}</li>
            <li> {{ t('hbs_handoff_step3_other') }}</li>
            <li> {{ t('hbs_handoff_step4_web') }}</li>
          </ol>
        </div>

        <!--        COMMON COPY-->

        <div class="row stroke-top stroke-bottom q-p-sm mt-24 semibold">
          <div class="col">
            <q-expansion-item :label="t('hbs_post_completion_title')" expand-icon="fa-regular fa-angle-down"
              expanded-icon="fa-regular fa-angle-up"> <!--Once you've completed the steps above-->
              <q-card>
                <q-card-section class="body-text">{{ t('hbs_post_completion_body') }}</q-card-section>
              </q-card>
            </q-expansion-item>
          </div>
        </div>
        <div class="row mt-40">
          <zbm-btn :color="$zb.enums.ButtonStdColorEnum.accent" :label="t('gen_ok')" :outline="true"
            :width="$zb.enums.ButtonWidthEnum.wFull" :height="$zb.enums.ButtonHeightEnum.lg" @click="onNextClick" />
        </div>
        <div class="row q-mt-xs">
          <zbm-btn :color="$zb.enums.ButtonStdColorEnum.whiteText"
            :label="t('hbs_computer_requirements_send_instructions')" :outline="true"
            :width="$zb.enums.ButtonWidthEnum.wFull" :height="$zb.enums.ButtonHeightEnum.lg" @click="onSendClick" />

          <a :href="patientConfig?.supportAssetLinks?.piqSetupInstructionPdfUri" target='_blank'
            ref="instructionsLink"></a>
        </div>
      </template>
    </MultiViewLayout>

    <the-abandon-modal v-model="showAbandon" />
    <the-contact-modal v-model="showContact" />

  </q-page>

</template>
